import React, { useEffect } from 'react'
import { motion } from 'framer-motion'
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  Check,
  MessageCircle,
  Phone,
  Search,
  Send,
} from 'lucide-react'
import { COMPANY_INFO, IMAGES, THANK_YOU_CONTENT, WHATSAPP_CONFIG } from '../constants'
import Footer from './Footer'

const stepIcons = [Search, Send, Phone]

const ThankYouPage: React.FC = () => {
  useEffect(() => {
    document.title = 'Gracias | Bavarian Real Estate'

    const metaDescription = document.querySelector('meta[name="description"]')
    metaDescription?.setAttribute(
      'content',
      'Gracias por contactar a Bavarian Real Estate. Un asesor se pondrá en contacto contigo pronto.'
    )

    const robotsMeta = document.querySelector('meta[name="robots"]')
    robotsMeta?.setAttribute('content', 'noindex, nofollow')

    const canonicalLink = document.querySelector('link[rel="canonical"]')
    canonicalLink?.setAttribute('href', `${window.location.origin}/thank-you`)

    const trackingWindow = window as Window & {
      fbq?: (...args: unknown[]) => void
      dataLayer?: Array<Record<string, unknown>>
    }

    trackingWindow.fbq?.('track', 'Lead')
    trackingWindow.dataLayer?.push({ event: 'lead' })
  }, [])

  const whatsappUrl = `https://wa.me/${WHATSAPP_CONFIG.phoneNumber}?text=${encodeURIComponent(
    WHATSAPP_CONFIG.message
  )}`
  const hasCalendlyLink = COMPANY_INFO.calendlyLink.startsWith('http')

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="fixed inset-x-0 top-0 z-50 border-b border-white/10 bg-white/90 shadow-sm backdrop-blur-xl">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <a
            href="/"
            className="flex items-center rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
            aria-label="Volver al inicio de Bavarian Real Estate"
          >
            <img src={IMAGES.logo} alt="Bavarian Real Estate Logo" className="h-10 w-auto" />
          </a>
        </div>
      </header>

      <main className="flex-1">
        <section
          className="relative min-h-[560px] overflow-hidden pt-20"
          aria-labelledby="thank-you-title"
        >
          <div className="absolute inset-0 z-0">
            <img
              src={IMAGES.hero}
              alt="Propiedades de lujo en República Dominicana"
              className="h-full w-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/60 to-black/40" />
          </div>

          <div className="relative z-10 mx-auto flex min-h-[480px] max-w-5xl items-center px-4 py-20 text-center sm:px-6 lg:px-8">
            <div className="mx-auto max-w-3xl">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                className="animate-pulse-glow mx-auto mb-8 flex h-24 w-24 items-center justify-center rounded-full bg-accent text-white shadow-lg shadow-accent/30"
              >
                <Check className="h-12 w-12" aria-hidden={true} />
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              >
                <span className="mb-5 block text-sm font-semibold uppercase tracking-[0.24em] text-accent-light">
                  {THANK_YOU_CONTENT.eyebrow}
                </span>
                <h1
                  id="thank-you-title"
                  className="accent-line accent-line-center mb-8 pt-2 text-4xl font-extrabold leading-tight tracking-tight text-white md:text-6xl"
                >
                  {THANK_YOU_CONTENT.headline}
                </h1>
                <p className="mx-auto max-w-2xl text-lg font-light leading-relaxed text-white/90 md:text-xl">
                  {THANK_YOU_CONTENT.message}
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        <section className="relative z-20 px-4 pb-20 sm:px-6 md:pb-28 lg:px-8">
          <div className="mx-auto -mt-24 max-w-5xl">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
              className="shadow-luxury rounded-2xl border border-border-light bg-white p-6 text-left md:p-10 lg:p-12"
            >
              <h2 className="mb-8 text-center text-2xl font-bold text-primary md:text-3xl">
                {THANK_YOU_CONTENT.nextStepsTitle}
              </h2>
              <div className="mb-10 grid gap-5 md:grid-cols-3">
                {THANK_YOU_CONTENT.nextSteps.map((step, index) => (
                  <motion.div
                    key={step}
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.45,
                      delay: 0.28 + index * 0.08,
                      ease: [0.22, 1, 0.36, 1],
                    }}
                    className="card-hover rounded-xl border border-t-4 border-border-light border-t-accent bg-white p-6 shadow-sm"
                  >
                    <div className="mb-5 flex items-center justify-between">
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent/10 text-accent">
                        {React.createElement(stepIcons[index] ?? Check, {
                          className: 'h-6 w-6',
                          'aria-hidden': true,
                        })}
                      </div>
                      <span className="text-3xl font-extrabold text-secondary-dark/60">
                        0{index + 1}
                      </span>
                    </div>
                    <p className="leading-relaxed text-text-secondary">{step}</p>
                  </motion.div>
                ))}
              </div>

              <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex w-full items-center justify-center rounded-lg bg-[#25D366] px-8 py-4 font-semibold text-white shadow-md transition-all duration-300 hover:bg-[#128C7E] hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-[#25D366] focus-visible:ring-offset-2 sm:w-auto"
                >
                  <MessageCircle className="mr-2 h-5 w-5" aria-hidden={true} />
                  {THANK_YOU_CONTENT.whatsappLabel}
                  <ArrowRight className="btn-arrow ml-2 h-5 w-5" aria-hidden={true} />
                </a>

                {hasCalendlyLink && (
                  <a
                    href={COMPANY_INFO.calendlyLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group inline-flex w-full items-center justify-center rounded-lg bg-accent px-8 py-4 font-semibold text-white shadow-md transition-all duration-300 hover:bg-accent-dark hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 sm:w-auto"
                  >
                    <Calendar className="mr-2 h-5 w-5" aria-hidden={true} />
                    {THANK_YOU_CONTENT.calendarLabel}
                    <ArrowRight className="btn-arrow ml-2 h-5 w-5" aria-hidden={true} />
                  </a>
                )}

                <a
                  href="/"
                  className="inline-flex w-full items-center justify-center rounded-lg border-2 border-primary px-8 py-4 font-semibold text-primary transition-all duration-300 hover:bg-primary hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 sm:w-auto"
                >
                  <ArrowLeft className="mr-2 h-5 w-5" aria-hidden={true} />
                  {THANK_YOU_CONTENT.homeLabel}
                </a>
              </div>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  )
}

export default ThankYouPage
